import React, { useState, useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../services/apiClient';
import { tenantNameToSlug } from '../../utils/tenantSlug';

// ADM WES / platform-admin signal. Mirrors the bypass used in ProtectedRoute
// and in the backend permission gates: an ADM WES sees every tenant by design
// (see `.cursor/rules` and the org-context flowchart), so OrganizationRoute
// must not block them when they navigate into another org's URL.
const isPlatformAdmin = (u) =>
  !!u?.platform_roles?.includes('admin_wes')
  || u?.is_superuser === true
  || u?.is_platform_admin === true;

const OrganizationRoute = ({ children }) => {
  const { orgName } = useParams();
  const location = useLocation();
  const { authenticated, loading: authLoading, user } = useAuth();
  const [resolving, setResolving] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const resolveOrganization = async () => {
      try {
        if (authenticated && user) {
          const userOrgName = tenantNameToSlug(user.tenant_name);

          // ADM WES bypass: platform admins reach every tenant. We still try
          // to surface the visited org's name (best-effort) so downstream UI
          // shows the correct label, but a failure here is non-fatal.
          if (isPlatformAdmin(user)) {
            if (orgName === userOrgName) {
              setOrgData({ name: user.tenant_name, id: user.tenant_id, hasAccess: true });
              setResolving(false);
              return;
            }
            try {
              const response = await apiClient.get(`/tenants/resolve/${orgName}`);
              setOrgData({
                name: response?.data?.name || user.tenant_name,
                id: response?.data?.tenant_id || user.tenant_id,
                hasAccess: true,
              });
            } catch (resolveErr) {
              if (resolveErr?.response?.status === 404) {
                setError('ORG_NOT_FOUND');
                setResolving(false);
                return;
              }
              setOrgData({ name: orgName, id: null, hasAccess: true });
            }
            setResolving(false);
            return;
          }

          if (orgName === userOrgName) {
            setOrgData({
              name: user.tenant_name,
              id: user.tenant_id,
              hasAccess: true
            });
            setResolving(false);
            return;
          } else {
            setError('ACCESS_DENIED');
            setResolving(false);
            return;
          }
        }
        
        // User is not authenticated - check if org exists and redirect to login
        try {
          const response = await apiClient.get(`/tenants/resolve/${orgName}`);
          if (response.data) {
            setOrgData({
              name: response.data.name,
              id: response.data.tenant_id,
              hasAccess: false
            });
          }
        } catch (error) {
          if (error.response?.status === 404) {
            setError('ORG_NOT_FOUND');
          } else {
            setError('RESOLVE_ERROR');
          }
        }
      } catch (error) {
        console.error('Error resolving organization:', error);
        setError('RESOLVE_ERROR');
      } finally {
        setResolving(false);
      }
    };

    if (!authLoading) {
      resolveOrganization();
    }
  }, [orgName, authenticated, user, authLoading]);

  // Show loading state
  if (authLoading || resolving) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {authLoading ? 'Checking authentication...' : 'Resolving organization...'}
        </Typography>
      </Box>
    );
  }

  // Handle errors
  if (error === 'ORG_NOT_FOUND') {
    return <Navigate to="/404" replace />;
  }

  if (error === 'ACCESS_DENIED') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (error === 'RESOLVE_ERROR') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          textAlign: 'center',
          px: 3
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Unable to Access Organization
        </Typography>
        <Typography variant="body1" color="text.secondary">
          There was an error accessing this organization. Please try again later.
        </Typography>
      </Box>
    );
  }

  // User is not authenticated - redirect to login with return URL
  if (!authenticated) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate 
        to="/login" 
        state={{ 
          from: location,
          returnUrl,
          orgName 
        }} 
        replace 
      />
    );
  }

  // User is authenticated and has access to the org
  if (orgData?.hasAccess) {
    return children;
  }

  // Fallback - should not reach here
  return <Navigate to="/unauthorized" replace />;
};

export default OrganizationRoute;